/**
 * HealLanguagesButton — Studio-header admin tool.
 * Opens a dialog with three choices: pre-render all missing languages,
 * fix only stuck tracks, or cancel.
 */
import { useState } from "react";
import axios from "axios";
import { Languages, Loader2, Wrench, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HealLanguagesButton({ adminToken }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const run = async (mode) => {
    setOpen(false);
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/heal-languages`,
        { mode },
        { headers: { "X-Admin-Token": adminToken || "storylo-admin" } }
      );
      toast.success(
        `Healer running — ${data.newly_enqueued} newly queued, ${data.waiting_in_queue} waiting, ${data.kicked_now} restarted right now. It keeps rendering in the background.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Heal request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={busy}
        className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-md border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] inline-flex items-center gap-1.5 disabled:opacity-40"
        data-testid="heal-languages-btn"
        title="Restart stuck language dubs, or pre-render every language for the whole library"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
        Heal languages
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="heal-languages-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="w-4 h-4" /> Heal languages
            </DialogTitle>
            <DialogDescription>Choose what the healer should do.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <button
              onClick={() => run("all")}
              className="w-full text-left p-3 rounded-lg border border-white/15 hover:border-[color:var(--accent)] transition-colors"
              data-testid="heal-prerender-all-btn"
            >
              <div className="flex items-center gap-2 font-medium text-sm">
                <Sparkles className="w-4 h-4 text-[color:var(--accent)]" /> Pre-render all
              </div>
              <p className="text-xs opacity-70 mt-1">
                Queue every missing language for every story. Runs in the background for hours; feeds fill up automatically.
              </p>
            </button>

            <button
              onClick={() => run("stuck")}
              className="w-full text-left p-3 rounded-lg border border-white/15 hover:border-[color:var(--accent)] transition-colors"
              data-testid="heal-fix-stuck-btn"
            >
              <div className="flex items-center gap-2 font-medium text-sm">
                <Wrench className="w-4 h-4 text-[color:var(--accent)]" /> Fix stuck only
              </div>
              <p className="text-xs opacity-70 mt-1">
                Only restart language tracks that got stuck mid-render.
              </p>
            </button>
          </div>

          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              className="text-xs uppercase tracking-wider px-4 py-2 rounded-md border border-white/15 hover:bg-white/5"
              data-testid="heal-cancel-btn"
            >
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
